import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { X, Save, Upload, AlertCircle, Film, Loader2 } from "lucide-react";
import { api } from "../lib/api";

const MAX_GENRES = 3;
const MAX_POSTER_MB = 10;

const fmtTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
};

/**
 * Modal for creators to edit their own movie: title, description, genres, poster.
 * Approved titles flip to 'pending' on any material change (backend enforces this).
 */
export default function EditMovieModal({ movie, onClose }) {
  const [title, setTitle] = useState(movie.title || "");
  const [description, setDescription] = useState(movie.description || "");
  const [selectedGenres, setSelectedGenres] = useState(
    movie.genres && movie.genres.length ? movie.genres : (movie.genre ? [movie.genre] : []),
  );
  const [genreCatalog, setGenreCatalog] = useState([]);
  const [poster, setPoster] = useState(null);
  const [posterPreview, setPosterPreview] = useState(null);
  const [posterSource, setPosterSource] = useState("");   // "" | "uploaded" | "frame"
  const [frameBusy, setFrameBusy] = useState(false);
  const [frameCandidates, setFrameCandidates] = useState([]);
  const [frameSelected, setFrameSelected] = useState(null);   // time_seconds picked
  const [posterCacheBust, setPosterCacheBust] = useState(0);  // bump to reload /media/poster
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/genres").then((r) => setGenreCatalog(r.data || [])).catch(() => setGenreCatalog([]));
  }, []);

  // Fetch Cloudflare-served frame candidates via backend (resolves duration
  // from CF meta if the movie doc is missing it, and avoids CORS issues).
  useEffect(() => {
    if (!movie.cloudflare_uid) return;
    api.get(`/movies/${movie.id}/frame-candidates`).then((r) => {
      setFrameCandidates(r.data?.candidates || []);
    }).catch(() => setFrameCandidates([]));
  }, [movie.id, movie.cloudflare_uid]);

  const toggleGenre = (name) => {
    setSelectedGenres((prev) => {
      if (prev.includes(name)) return prev.filter((g) => g !== name);
      if (prev.length >= MAX_GENRES) {
        toast.error(`Up to ${MAX_GENRES} genres.`);
        return prev;
      }
      return [...prev, name];
    });
  };

  const onPosterChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Poster must be an image."); return; }
    if (f.size > MAX_POSTER_MB * 1024 * 1024) { toast.error(`Poster must be under ${MAX_POSTER_MB} MB.`); return; }
    setPoster(f);
    setPosterPreview(URL.createObjectURL(f));
    setPosterSource("uploaded");
    setFrameSelected(null);
  };

  const pickFrame = useCallback(async (candidate) => {
    // Server-side proxy: backend fetches the Cloudflare thumbnail, saves it as
    // the movie's poster, and — if approved — flips status back to pending.
    // This avoids CORS restrictions on the browser-side blob read.
    if (frameBusy || busy) return;
    setFrameBusy(true);
    try {
      await api.post(`/movies/${movie.id}/poster-from-frame`, {
        time_seconds: candidate.time_seconds,
      });
      setFrameSelected(candidate.time_seconds);
      setPosterSource("frame");
      setPoster(null);           // clear any uploaded file — frame won this round
      setPosterPreview(null);
      setPosterCacheBust((n) => n + 1);
      toast.success(`Frame at ${fmtTime(candidate.time_seconds)} is now the poster.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't grab that frame — try another.");
    } finally {
      setFrameBusy(false);
    }
  }, [movie.id, frameBusy, busy]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required."); return; }
    if (selectedGenres.length === 0) { toast.error("Pick at least one genre."); return; }
    setBusy(true);
    try {
      // 1. PATCH title/desc/genres
      const changedTitle = title.trim() !== (movie.title || "");
      const changedDesc = description.trim() !== (movie.description || "");
      const prevGenres = (movie.genres && movie.genres.length ? movie.genres : [movie.genre]).filter(Boolean);
      const changedGenres = JSON.stringify([...selectedGenres].sort()) !== JSON.stringify([...prevGenres].sort());
      if (changedTitle || changedDesc || changedGenres) {
        await api.patch(`/movies/${movie.id}`, {
          title: title.trim(),
          description: description.trim(),
          genres: selectedGenres,
        });
      }
      // 2. POST new *uploaded* poster if provided. Frame-based posters have
      //    already been persisted server-side by pickFrame().
      if (poster) {
        const fd = new FormData();
        fd.append("poster", poster);
        await api.post(`/movies/${movie.id}/poster`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      const material = changedTitle || changedDesc || changedGenres || poster || posterSource === "frame";
      if (material && movie.status === "approved") {
        toast.success("Saved. Your title is back in the review queue.");
      } else {
        toast.success("Saved.");
      }
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save changes.");
    } finally {
      setBusy(false);
    }
  };

  const posterSrc = posterPreview
    ? posterPreview
    : `${process.env.REACT_APP_BACKEND_URL}/api/media/poster/${movie.id}${posterCacheBust ? `?v=${posterCacheBust}` : ""}`;

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" data-testid="edit-movie-modal">
      <div className="card-surface !bg-[#0a0a0a] max-w-2xl w-full my-8 p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/40 hover:text-white transition" data-testid="edit-modal-close" aria-label="Close">
          <X className="h-5 w-5" />
        </button>
        <p className="text-eyebrow text-[#E65F2B] mb-1">Edit title</p>
        <h2 className="font-display text-3xl font-black tracking-tighter mb-6">{movie.title}</h2>

        {movie.status === "approved" && (
          <div className="mb-6 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2 text-sm" data-testid="edit-modal-pending-warning">
            <AlertCircle className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" />
            <p className="text-yellow-200/80">
              This film is <b>live</b>. Any material change (title, description, genres, or poster) sends it back to the moderation queue.
            </p>
          </div>
        )}

        <div className="space-y-5">
          <div>
            <label className="text-eyebrow mb-2 block">Title</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={200}
              className="input-dark"
              disabled={busy}
              data-testid="edit-title-input"
            />
          </div>
          <div>
            <label className="text-eyebrow mb-2 block">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              maxLength={5000}
              className="input-dark resize-none"
              disabled={busy}
              data-testid="edit-description-input"
            />
          </div>
          <div>
            <div className="flex items-baseline justify-between mb-2">
              <label className="text-eyebrow">Genres · up to {MAX_GENRES}</label>
              <span className="text-xs text-white/40" data-testid="edit-genre-counter">{selectedGenres.length} / {MAX_GENRES}</span>
            </div>
            <div className="flex flex-wrap gap-2" data-testid="edit-genre-chips">
              {genreCatalog.map((g) => {
                const active = selectedGenres.includes(g.name);
                const capped = !active && selectedGenres.length >= MAX_GENRES;
                return (
                  <button
                    type="button"
                    key={g.id}
                    onClick={() => toggleGenre(g.name)}
                    disabled={busy || capped}
                    className={`px-3 py-1.5 rounded-full border text-xs transition ${
                      active
                        ? "bg-[#E65F2B] border-[#E65F2B] text-white"
                        : capped
                        ? "border-white/5 text-white/20 cursor-not-allowed"
                        : "border-white/10 text-white/60 hover:border-white/30 hover:text-white"
                    }`}
                    data-testid={`edit-genre-chip-${g.slug}`}
                  >
                    {g.name}
                  </button>
                );
              })}
            </div>
          </div>
          <div>
            <label className="text-eyebrow mb-2 block">Poster · replace (optional)</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-36 rounded-md bg-black overflow-hidden shrink-0 border border-white/10">
                <img
                  key={posterSrc}
                  src={posterSrc}
                  alt="poster"
                  className="h-full w-full object-cover"
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
              </div>
              <label className="ghost-btn cursor-pointer" data-testid="edit-poster-input-label">
                <Upload className="h-4 w-4" /> {poster ? "Change again" : "Upload custom poster"}
                <input type="file" accept="image/*" onChange={onPosterChange} className="hidden" disabled={busy} data-testid="edit-poster-input" />
              </label>
              {poster && (
                <button
                  onClick={() => { setPoster(null); setPosterPreview(null); setPosterSource(""); }}
                  className="text-xs text-white/40 hover:text-white transition"
                  data-testid="edit-poster-clear"
                >
                  Reset
                </button>
              )}
            </div>
            <p className="text-xs text-white/40 mt-2">
              JPG or PNG · under {MAX_POSTER_MB} MB.
              {posterSource === "frame" && frameSelected != null && (
                <span className="text-[#EAB308] ml-1">· frame at {fmtTime(frameSelected)} applied</span>
              )}
              {posterSource === "uploaded" && poster && (
                <span className="text-[#E65F2B] ml-1">· using uploaded file (saves on click Save)</span>
              )}
            </p>

            {/* Frame picker — server-side proxy fetches each Cloudflare Stream
                thumbnail and saves it as the poster in one click. Displays are
                fine via <img> from Cloudflare's customer subdomain. */}
            {movie.cloudflare_uid && frameCandidates.length > 0 && (
              <div className="mt-4" data-testid="edit-frame-picker">
                <div className="flex items-center gap-2 mb-2">
                  <Film className="h-3.5 w-3.5 text-[#EAB308]" />
                  <p className="text-eyebrow">Or pick a frame from your video</p>
                  {frameBusy && <Loader2 className="h-3 w-3 animate-spin text-white/40" />}
                </div>
                <div className="grid grid-cols-5 gap-2">
                  {frameCandidates.map((c, i) => {
                    const isSelected = frameSelected === c.time_seconds;
                    return (
                      <button
                        key={c.time_seconds}
                        type="button"
                        onClick={() => pickFrame(c)}
                        disabled={busy || frameBusy}
                        className={`group relative rounded-md overflow-hidden bg-black border transition disabled:opacity-40 disabled:cursor-not-allowed ${
                          isSelected ? "border-[#EAB308] ring-2 ring-[#EAB308]/40" : "border-white/10 hover:border-[#EAB308]/60"
                        }`}
                        style={{ aspectRatio: "16 / 9" }}
                        data-testid={`edit-frame-candidate-${i + 1}`}
                        title={`Use frame at ${fmtTime(c.time_seconds)}`}
                      >
                        <img
                          src={c.thumbnail_url}
                          alt={`Frame at ${fmtTime(c.time_seconds)}`}
                          loading="lazy"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          onError={(e) => { e.currentTarget.style.opacity = "0.15"; }}
                        />
                        <span className="absolute bottom-1 right-1 text-[10px] bg-black/70 text-white px-1 py-0.5 rounded tabular-nums">{fmtTime(c.time_seconds)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
            {movie.cloudflare_uid && frameCandidates.length === 0 && (
              <p className="text-xs text-white/30 mt-3" data-testid="edit-frame-picker-empty">
                Frame picker unavailable — video is still processing or duration unknown.
              </p>
            )}
          </div>
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={save} disabled={busy} className="ember-btn disabled:opacity-40" data-testid="edit-save-button">
            <Save className="h-4 w-4" /> {busy ? "Saving..." : "Save changes"}
          </button>
          <button onClick={onClose} disabled={busy} className="ghost-btn" data-testid="edit-cancel-button">Cancel</button>
        </div>
      </div>
    </div>
  );
}
